import React, { useEffect, useMemo, useState } from "react";
import { Check, ExternalLink, Monitor, Smartphone } from "lucide-react";
import TenantPreferencesService from "../../_services/TenantPreferencesService";
import Config from "../../_config/config";

/**
 * Student Start Page picker — Tenant Preferences → Start Page tab.
 *
 * Sets `homeRoute` in the tenant's app-properties.json (saved with
 * mode="merge" so nothing else in the file is touched). ilearn reads
 * homeRoute at boot: it redirects /dashboard there and points the navbar
 * Home link at it. Unset means the app default, /dashboard.
 */

type OptionKey = "hub" | "dash" | "cp" | "custom";

interface StartPageOption {
  key: OptionKey;
  title: string;
  route: string;
  description: string;
  recommended?: boolean;
}

const OPTIONS: StartPageOption[] = [
  {
    key: "hub",
    title: "Student Hub",
    route: "/student-hub",
    description: "Personal home: greeting hero, goal ring, quick actions, and what's-next cards.",
    recommended: true,
  },
  {
    key: "dash",
    title: "Classic Dashboard",
    route: "/dashboard",
    description: "The familiar tile grid — every module one tap away. Best for existing tenants.",
  },
  {
    key: "cp",
    title: "Control Panel",
    route: "/control-panel",
    description: "Task-first list view. Suits exam-season or admin-heavy programmes.",
  },
];

// Parameterless student-facing routes from ilearn's route table
// (ilearn/src/pages/main.jsx). Used to validate the custom option — keep in
// sync when a new student page is a plausible landing target.
const KNOWN_ILEARN_ROUTES: Record<string, string> = {
  "/student-hub": "Student Hub",
  "/dashboard": "Classic Dashboard",
  "/control-panel": "Control Panel",
  "/assignments": "Assignments",
  "/tutorials": "Tutorials",
  "/play-ground": "Practice Zone",
  "/games": "Games Zone",
  "/my-certificates": "My Certificates & Badges",
  "/notes": "Notes",
  "/grade-report": "Grade Report",
  "/application-tracker": "Application Tracker",
  "/help-center": "Help Center",
  "/photo-gallery": "Photo Gallery",
  "/zoom-classes": "Zoom Classes",
  "/proctored-tests": "Proctored Tests",
  "/diagnostics": "Diagnostic Test",
  "/my-doubts": "My Doubts",
  "/ai-workspace": "AI Workspace",
  "/chat": "Chat",
  "/wallet": "Coin Wallet",
  "/leaderboard": "Leaderboard",
  "/notification": "Notifications",
  "/profile": "Profile",
  "/repository": "Repository",
  "/mentoring": "Mentoring",
  "/bookmarked-videos": "Bookmarked Videos",
};

const APP_DEFAULT_ROUTE = "/dashboard";

const ilearnHost = (Config.ilearnUrl || "https://example.ilearn.edusquares.com/")
  .replace(/^https?:\/\//, "")
  .replace(/\/$/, "");

interface Props {
  onError: (message: string | null) => void;
  onSuccess: (message: string | null) => void;
}

const StartPagePicker: React.FC<Props> = ({ onError, onSuccess }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // homeRoute as stored (null = key absent = app default /dashboard)
  const [currentRoute, setCurrentRoute] = useState<string | null>(null);
  const [selectedKey, setSelectedKey] = useState<OptionKey>("dash");
  const [customRoute, setCustomRoute] = useState("/");
  const [device, setDevice] = useState<"desktop" | "mobile">("desktop");

  useEffect(() => {
    const load = async () => {
      try {
        const props = await TenantPreferencesService.getAppProperties();
        // Prefer the camelCase key this picker writes; fall back to the legacy
        // hyphenated `Home-Route` older tenant files carry so the current value
        // still reflects here even before a first re-save.
        const rawRoute = props?.homeRoute ?? props?.["Home-Route"];
        const route = typeof rawRoute === "string" && rawRoute.trim()
          ? rawRoute.trim()
          : null;
        setCurrentRoute(route);
        const effective = route || APP_DEFAULT_ROUTE;
        const match = OPTIONS.find((o) => o.route === effective);
        if (match) {
          setSelectedKey(match.key);
        } else {
          setSelectedKey("custom");
          setCustomRoute(effective);
        }
      } catch (e: any) {
        onError(e?.response?.data?.error?.message || e?.message || "Failed to load app-properties");
      } finally {
        setLoading(false);
      }
    };
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const effectiveCurrent = currentRoute || APP_DEFAULT_ROUTE;
  const currentKey: OptionKey =
    OPTIONS.find((o) => o.route === effectiveCurrent)?.key ?? "custom";

  const selectedRoute = selectedKey === "custom"
    ? customRoute.trim()
    : OPTIONS.find((o) => o.key === selectedKey)!.route;

  const customState = useMemo(() => {
    if (selectedKey !== "custom") return null;
    const v = customRoute.trim();
    if (!v.startsWith("/") || /\s/.test(v)) {
      return { level: "error" as const, text: "Path must start with / and contain no spaces." };
    }
    if (KNOWN_ILEARN_ROUTES[v]) {
      return { level: "ok" as const, text: `Matches an iLearn page (${KNOWN_ILEARN_ROUTES[v]}).` };
    }
    return {
      level: "warn" as const,
      text: "Not in the known iLearn page list — double-check with Open live before saving. Students who land on a missing page see a blank screen.",
    };
  }, [selectedKey, customRoute]);

  const dirty = selectedRoute !== effectiveCurrent;
  const saveBlocked = saving || !dirty || customState?.level === "error";

  const handleSave = async () => {
    if (saveBlocked) return;
    setSaving(true);
    onError(null);
    onSuccess(null);
    try {
      await TenantPreferencesService.saveAppProperties({ homeRoute: selectedRoute }, "merge");
      setCurrentRoute(selectedRoute);
      onSuccess(`Start page saved — students land on ${selectedRoute} from their next sign-in (no deploy needed).`);
    } catch (e: any) {
      onError(e?.response?.data?.error?.message || e?.message || "Failed to save start page");
    } finally {
      setSaving(false);
    }
  };

  const openLive = () => {
    const base = (Config.ilearnUrl || "").replace(/\/$/, "");
    window.open(`${base}${selectedRoute}`, "_blank", "noopener");
  };

  // ── Layout skeletons (pure CSS, no screenshots — never stale, no student data)

  const renderThumb = (key: OptionKey) => {
    if (key === "hub") {
      return (
        <div className="tp-sp-thumb">
          <div className="tp-sp-sk tp-sp-sk-brand" style={{ height: 26, borderRadius: 6, marginBottom: 5 }} />
          <div className="tp-sp-row" style={{ marginBottom: 5 }}>
            <div className="tp-sp-sk tp-sp-sk-soft tp-sp-flex1" style={{ height: 10, borderRadius: 99 }} />
            <div className="tp-sp-sk tp-sp-sk-soft tp-sp-flex1" style={{ height: 10, borderRadius: 99 }} />
            <div className="tp-sp-sk tp-sp-sk-soft tp-sp-flex1" style={{ height: 10, borderRadius: 99 }} />
          </div>
          <div className="tp-sp-row">
            <div className="tp-sp-sk tp-sp-sk-sky tp-sp-flex1" style={{ height: 26 }} />
            <div className="tp-sp-sk tp-sp-sk-soft tp-sp-flex1" style={{ height: 26 }} />
            <div className="tp-sp-sk tp-sp-sk-soft tp-sp-flex1" style={{ height: 26 }} />
          </div>
        </div>
      );
    }
    if (key === "dash") {
      return (
        <div className="tp-sp-thumb">
          <div className="tp-sp-sk tp-sp-sk-soft" style={{ height: 12, borderRadius: 6, marginBottom: 6 }} />
          <div className="tp-sp-row" style={{ marginBottom: 5 }}>
            <div className="tp-sp-sk tp-sp-sk-sky tp-sp-flex1" style={{ height: 24 }} />
            <div className="tp-sp-sk tp-sp-sk-soft tp-sp-flex1" style={{ height: 24 }} />
            <div className="tp-sp-sk tp-sp-sk-soft tp-sp-flex1" style={{ height: 24 }} />
          </div>
          <div className="tp-sp-row">
            <div className="tp-sp-sk tp-sp-sk-soft tp-sp-flex1" style={{ height: 24 }} />
            <div className="tp-sp-sk tp-sp-sk-sky tp-sp-flex1" style={{ height: 24 }} />
            <div className="tp-sp-sk tp-sp-sk-soft tp-sp-flex1" style={{ height: 24 }} />
          </div>
        </div>
      );
    }
    if (key === "cp") {
      return (
        <div className="tp-sp-thumb">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="tp-sp-row" style={{ marginBottom: i < 3 ? 5 : 0 }}>
              <div className={`tp-sp-sk ${i === 1 ? "tp-sp-sk-brand" : i === 3 ? "tp-sp-sk-soft" : "tp-sp-sk-sky"}`} style={{ width: 16, height: 16, borderRadius: 5 }} />
              <div className="tp-sp-sk tp-sp-sk-soft tp-sp-flex1" style={{ height: 16 }} />
            </div>
          ))}
        </div>
      );
    }
    return (
      <div className="tp-sp-thumb tp-sp-thumb-custom">
        <span>/&hellip;</span>
      </div>
    );
  };

  const renderStage = () => {
    if (selectedKey === "hub") {
      return (
        <>
          <div className="tp-sp-hero">
            <div className="tp-sp-avatar" />
            <div className="tp-sp-flex1">
              <div className="tp-sp-heroline" style={{ width: "45%", marginBottom: 7 }} />
              <div className="tp-sp-heroline" style={{ width: "28%", opacity: 0.7 }} />
            </div>
            <div className="tp-sp-ring" title="Goal progress ring" />
          </div>
          <div className="tp-sp-pillrow">
            {[0, 1, 2, 3].map((i) => <div key={i} className="tp-sp-pill" />)}
          </div>
          <div className="tp-sp-cardgrid">
            {["#7dd3fc", "#a78bfa", "#fbbf24", "#34d399"].map((c) => (
              <div key={c} className="tp-sp-mockcard">
                <div className="tp-sp-ic" style={{ background: c }} />
                <div className="tp-sp-ln" style={{ width: "70%" }} />
                <div className="tp-sp-ln" style={{ width: "45%" }} />
              </div>
            ))}
          </div>
        </>
      );
    }
    if (selectedKey === "dash") {
      return (
        <>
          <div className="tp-sp-navband">
            <div className="tp-sp-ln" style={{ width: 48 }} />
            <div className="tp-sp-ln" style={{ width: 48 }} />
            <div className="tp-sp-ln" style={{ width: 32 }} />
          </div>
          <div className="tp-sp-tilegrid">
            {["#7dd3fc", "#a78bfa", "#34d399", "#fbbf24", "#f472b6", "#818cf8", "#2dd4bf", "#fb923c"].map((c) => (
              <div key={c} className="tp-sp-tile">
                <div className="tp-sp-ic" style={{ background: c }} />
                <div className="tp-sp-ln" style={{ width: "60%" }} />
              </div>
            ))}
          </div>
        </>
      );
    }
    if (selectedKey === "cp") {
      return (
        <>
          <div className="tp-sp-navband" style={{ width: "55%" }}>
            <div className="tp-sp-ln" style={{ width: 48 }} />
          </div>
          {["#7dd3fc", "#a78bfa", "#34d399", "#fbbf24"].map((c, i) => (
            <div key={c} className="tp-sp-listrow">
              <div className="tp-sp-ic" style={{ background: c }} />
              <div className="tp-sp-flex1">
                <div className="tp-sp-ln" style={{ width: `${[55, 62, 48, 58][i]}%`, marginBottom: 5 }} />
                <div className="tp-sp-ln" style={{ width: `${[30, 26, 34, 22][i]}%`, opacity: 0.6 }} />
              </div>
            </div>
          ))}
        </>
      );
    }
    return (
      <div className="tp-sp-custom-stage">
        <label htmlFor="tp-sp-custom-route">
          Enter any iLearn path — checked against the app&apos;s known pages as you type.
        </label>
        <div className="tp-sp-custom-input">
          <span className="tp-sp-custom-prefix">{ilearnHost}</span>
          <input
            id="tp-sp-custom-route"
            value={customRoute}
            spellCheck={false}
            onChange={(e) => setCustomRoute(e.target.value)}
          />
        </div>
        {customState && (
          <span className={`tp-sp-hint tp-sp-hint-${customState.level}`}>
            {customState.level === "ok" ? "✓ " : "✗ "}{customState.text}
          </span>
        )}
      </div>
    );
  };

  if (loading) {
    return <div className="tp-loading">Loading start page…</div>;
  }

  const selectedTitle = selectedKey === "custom"
    ? "Custom route"
    : OPTIONS.find((o) => o.key === selectedKey)!.title;

  return (
    <div className="tp-sp">
      <div className="tp-sp-gallery" role="radiogroup" aria-label="Choose a start page">
        {OPTIONS.map((o) => (
          <button
            key={o.key}
            type="button"
            role="radio"
            aria-checked={selectedKey === o.key}
            className={`tp-sp-opt ${selectedKey === o.key ? "selected" : ""}`}
            onClick={() => setSelectedKey(o.key)}
          >
            <span className="tp-sp-badges">
              {currentKey === o.key && (
                <span className="tp-sp-badge tp-sp-badge-live">
                  {currentRoute ? "Active now" : "App default"}
                </span>
              )}
              {o.recommended && currentKey !== o.key && (
                <span className="tp-sp-badge tp-sp-badge-reco">Recommended</span>
              )}
            </span>
            <span className="tp-sp-check"><Check size={12} /></span>
            {renderThumb(o.key)}
            <h4>{o.title} <span className="tp-sp-route">{o.route}</span></h4>
            <p>{o.description}</p>
          </button>
        ))}

        <button
          type="button"
          role="radio"
          aria-checked={selectedKey === "custom"}
          className={`tp-sp-opt ${selectedKey === "custom" ? "selected" : ""}`}
          onClick={() => setSelectedKey("custom")}
        >
          <span className="tp-sp-badges">
            {currentKey === "custom" && <span className="tp-sp-badge tp-sp-badge-live">Active now</span>}
          </span>
          <span className="tp-sp-check"><Check size={12} /></span>
          {renderThumb("custom")}
          <h4>Custom route</h4>
          <p>Point students at any iLearn page — validated against the app&apos;s route table.</p>
        </button>
      </div>

      <div className="tp-card tp-sp-preview">
        <div className="tp-sp-preview-head">
          <h3>Preview — {selectedTitle}</h3>
          <div className="tp-sp-spacer" />
          <div className="tp-sp-seg" role="group" aria-label="Preview device">
            <button
              type="button"
              className={device === "desktop" ? "on" : ""}
              onClick={() => setDevice("desktop")}
            >
              <Monitor size={13} /> Desktop
            </button>
            <button
              type="button"
              className={device === "mobile" ? "on" : ""}
              onClick={() => setDevice("mobile")}
            >
              <Smartphone size={13} /> Mobile
            </button>
          </div>
          <button type="button" className="tp-sp-openlive" onClick={openLive}>
            Open live in iLearn <ExternalLink size={12} />
          </button>
        </div>

        <div className={`tp-sp-browser ${device === "mobile" ? "mobile" : ""}`}>
          <div className="tp-sp-chrome">
            <span className="tp-sp-b" /><span className="tp-sp-b" /><span className="tp-sp-b" />
            <span className="tp-sp-url">{ilearnHost}<b>{selectedRoute || "/"}</b></span>
          </div>
          <div className="tp-sp-stage">{renderStage()}</div>
        </div>
        <p className="tp-sp-preview-note">
          Layout sketch, not a screenshot — use Open live to see the real page with this tenant&apos;s data.
        </p>
      </div>

      <div className="tp-card tp-sp-savebar">
        <span className="tp-sp-delta">
          {dirty ? (
            <>
              Students will land on <span className="tp-sp-chip">{effectiveCurrent}</span>
              <span className="tp-sp-arrow">→</span>
              <span className="tp-sp-chip to">{selectedRoute || "/"}</span>
            </>
          ) : (
            <>
              Students land on <span className="tp-sp-chip">{effectiveCurrent}</span>
              {!currentRoute && " (app default)"} — no change pending.
            </>
          )}
        </span>
        <button
          type="button"
          className="tp-btn tp-btn-primary tp-sp-save"
          disabled={saveBlocked}
          onClick={() => void handleSave()}
        >
          {saving ? "Saving…" : "Save Start Page"}
        </button>
        <span className="tp-sp-savenote">
          Writes <code>homeRoute</code> to app-properties.json (uploaded with no-cache).
          Students see the new start page on their next sign-in — no deploy needed.
        </span>
      </div>
    </div>
  );
};

export default StartPagePicker;
